import React, { useState, useEffect, useCallback,Fragment } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { removeLawyer, fetchLawyerInformation, fetchLawyerCases, fetchLawyerDocuments, fetchLawyerBilling } from '../../store/actions/LawyerAction';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import Badge from '../shared/TailwindComponents/Badge';
import ListItem from '../shared/TailwindComponents/ListItem';
import { Menu, Transition } from '@headlessui/react';
import { EllipsisVerticalIcon, TrashIcon, StarIcon } from '@heroicons/react/24/outline';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { convertLongDateWithTranslation } from '../shared/DateConverter';
import { formatCamelCase } from '../../utils/functions';
import { PiBriefcaseLight, PiCaretLeftLight, PiFileTextLight, PiFolderLight, PiHandshakeLight, PiEnvelopeLight, PiPhoneLight, PiClockLight, PiCurrencyCircleDollarLight, PiUsersLight, PiTranslateLight, PiTagLight, PiCertificateLight, PiTrophyLight, PiBookOpenLight, PiTrashLight } from 'react-icons/pi';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import TabStatusNav, { TabItem } from '../shared/TailwindComponents/TabStatusNav';
import GlassCard from '../shared/brand/GlassCard';
import ClientEngagementTerms from './ClientEngagementTerms';

interface RouteParams {
  lawyerId: string;
}

interface Lawyer {
  id: string;
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNumber: string;
  ProfilePicture?: string;
  OrganizationName?: string;
  Bio?: string;
  ExperienceYears?: number;
  HourlyRate?: number;
  Specializations?: string[];
  LanguagesSpoken?: string[];
  Education?: string;
  Certifications?: Array<{ title: string; fileUrl?: string }>;
  Awards?: Array<{ title: string; fileUrl?: string }>;
  Publications?: Array<{ title: string; fileUrl?: string }>;
  Role?: string;
  UserTypeName?: string;
  RatingsAverage?: number;
  RatingsCount?: number;
}

interface Case {
  CaseID: string;
  CaseName: string;
  case_status: string;
  created_at: string;
}

const LawyerDetails: React.FC = () => {
  const { lawyerId } = useParams<RouteParams>();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { setBreadcrumbs } = useBreadcrumbs();

  const [lawyerInfo, setLawyerInfo] = useState<Lawyer | null>(null);
  const [cases, setCases] = useState<Case[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [billing, setBilling] = useState<any>(null);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [isLoadingCases, setIsLoadingCases] = useState(false);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isLoadingBilling, setIsLoadingBilling] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'information';
  const [activeTab, setActiveTab] = useState(initialTab);

  const tabs: TabItem[] = [
    { label: t('Information'), value: 'information', icon: <PiBriefcaseLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Engagement Terms'), value: 'engagement', icon: <PiHandshakeLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Cases'), value: 'cases', icon: <PiFileTextLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Documents'), value: 'documents', icon: <PiFolderLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Billing'), value: 'billing', icon: <CreditCardIcon className="h-5 w-5 text-gray-500" /> },
  ];

  const fetchData = useCallback(async () => {
    setIsLoadingInfo(true);
    const info = await dispatch(fetchLawyerInformation(lawyerId));
    setLawyerInfo(info);
    setIsLoadingInfo(false);
  }, [dispatch, lawyerId]);

  const fetchCasesData = useCallback(async () => {
    setIsLoadingCases(true);
    const caseData = await dispatch(fetchLawyerCases(lawyerId));
    setCases(caseData);
    setIsLoadingCases(false);
  }, [dispatch, lawyerId]);

  const fetchDocumentsData = useCallback(async () => {
    setIsLoadingDocuments(true);
    const docData = await dispatch(fetchLawyerDocuments(lawyerId));
    setDocuments(docData);
    setIsLoadingDocuments(false);
  }, [dispatch, lawyerId]);

  const fetchBillingData = useCallback(async () => {
    setIsLoadingBilling(true);
    const billingData = await dispatch(fetchLawyerBilling(lawyerId));
    setBilling(billingData);
    setIsLoadingBilling(false);
  }, [dispatch, lawyerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'cases' && cases.length === 0) fetchCasesData();
    if (activeTab === 'documents' && documents.length === 0) fetchDocumentsData();
    if (activeTab === 'billing' && !billing) fetchBillingData();
  }, [activeTab, fetchCasesData, fetchDocumentsData, fetchBillingData]);

  useEffect(() => {
    if (lawyerInfo) {
      const fullName = `${lawyerInfo.FirstName || ''} ${lawyerInfo.LastName || ''}`.trim() || t('Unknown Lawyer');
      setBreadcrumbs([
        { name: t('Lawyers'), href: '/lawyers', current: false },
        { name: fullName, href: `/lawyers/${lawyerId}`, current: true },
      ]);
    }
    return () => setBreadcrumbs([]);
  }, [lawyerInfo, lawyerId, setBreadcrumbs, t]);

  const handleRemoveLawyer = () => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Remove Lawyer'),
        subMsg: t('This lawyer will be removed from your list. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        isDest: true,
        onConfirm: async () => {
          dispatch(hideModal());
          const success = await dispatch(removeLawyer(lawyerId));
          if (success) history.push('/lawyers');
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  };

  const handleCaseClick = (caseDoc: Case) => {
    history.push(`/matters/${caseDoc.CaseID}`);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    history.push(`/lawyers/${lawyerId}?tab=${tab}`);
  };

  const renderProfileSkeleton = () => <Skeleton variant="card" rows={4} />;

  const renderSectionSkeleton = () => <Skeleton variant="prose" rows={4} />;

  const renderProfile = () => {
    if (isLoadingInfo) return renderProfileSkeleton();
    if (!lawyerInfo) return <p className="text-gray-500">{t('No information available')}</p>;

    const ratingStars = [0, 1, 2, 3, 4];
    const avgRating = lawyerInfo.RatingsAverage || 0;
    const totalReviews = lawyerInfo.RatingsCount || 0;

    return (
      <div className="flex flex-col md:flex-row md:items-center md:space-x-6">
        <div className="flex-shrink-0 mb-4 md:mb-0">
          {lawyerInfo.ProfilePicture ? (
            <img
              src={lawyerInfo.ProfilePicture}
              alt={`${lawyerInfo.FirstName} ${lawyerInfo.LastName}`}
              className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700 transition-transform duration-200 hover:scale-105"
            />
          ) : (
            <div className="w-32 h-32 md:w-40 md:h-40 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-4xl font-bold text-gray-600 dark:text-gray-400 transition-transform duration-200 hover:scale-105">
              {lawyerInfo ? `${lawyerInfo.FirstName?.[0] || 'U'}${lawyerInfo.LastName?.[0] || 'L'}` : 'UL'}
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors duration-200">
              {`${lawyerInfo.FirstName || ''} ${lawyerInfo.LastName || ''}`.trim() || t('Unknown Lawyer')}
            </h2>
            <Menu as="div" className="relative inline-block text-start">
              <Menu.Button className="inline-flex justify-center px-2 py-2 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-100 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">
                <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
              </Menu.Button>
              <Transition as={Fragment}>
                <Menu.Items className="origin-top-right absolute end-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-700 z-50">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleRemoveLawyer}
                          className={`${active ? 'bg-danger-50 dark:bg-danger-700' : ''} group flex items-center px-4 py-2 text-sm text-danger-700 dark:text-danger-400 w-full text-start`}
                        >
                          <TrashIcon className="me-2 h-5 w-5" aria-hidden="true" />
                          {t('Remove Lawyer')}
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
          <div className="mt-3 flex items-center space-x-2">
            {ratingStars.map((_, idx) => (
              <StarIcon
                key={idx}
                className={avgRating > idx ? 'text-warning-400 h-5 w-5' : 'text-gray-300 h-5 w-5'}
              />
            ))}
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {avgRating.toFixed(1)} / 5.0 ({totalReviews} {t('Reviews')})
            </p>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {lawyerInfo.Email && (
              <div className="flex items-center space-x-2">
                <PiEnvelopeLight className="text-gray-500 w-5 h-5" />
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                  {lawyerInfo.Email || t('None')}
                </span>
              </div>
            )}
            {lawyerInfo.PhoneNumber && (
              <div className="flex items-center space-x-2">
                <PiPhoneLight className="text-gray-500 w-5 h-5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {lawyerInfo.PhoneNumber || t('None')}
                </span>
              </div>
            )}
            {lawyerInfo.ExperienceYears !== undefined && (
              <div className="flex items-center space-x-2">
                <PiClockLight className="text-gray-500 w-5 h-5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {lawyerInfo.ExperienceYears !== null ? `${lawyerInfo.ExperienceYears} ${t('years')}` : t('0')}
                </span>
              </div>
            )}
            {lawyerInfo.HourlyRate !== undefined && (
              <div className="flex items-center space-x-2">
                <PiCurrencyCircleDollarLight className="text-gray-500 w-5 h-5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {lawyerInfo.HourlyRate !== null ? `$${lawyerInfo.HourlyRate.toFixed(2)}/${t('hour')}` : t('0')}
                </span>
              </div>
            )}
            {lawyerInfo.OrganizationName && (
              <div className="flex items-center space-x-2">
                <PiUsersLight className="text-gray-500 w-5 h-5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {lawyerInfo.OrganizationName || t('None')}
                </span>
              </div>
            )}
            {lawyerInfo.Role && (
              <div className="flex items-center space-x-2">
                <PiBriefcaseLight className="text-gray-500 w-5 h-5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {lawyerInfo.Role || t('None')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderInformationTab = () => {
    if (isLoadingInfo) return renderSectionSkeleton();
    if (!lawyerInfo) return <p className="text-gray-500">{t('No information available')}</p>;

    const specializations = lawyerInfo.Specializations ?? [];
    const languagesSpoken = lawyerInfo.LanguagesSpoken ?? [];
    const certifications = lawyerInfo.Certifications ?? [];
    const awards = lawyerInfo.Awards ?? [];
    const publications = lawyerInfo.Publications ?? [];

    return (
      <div className="mt-2 transition-opacity duration-300">
        {lawyerInfo.Bio && (
          <GlassCard icon={<PiFileTextLight />} title={t('About')} className="mt-2">
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{lawyerInfo.Bio}</p>
          </GlassCard>
        )}
        {specializations.length > 0 && (
          <GlassCard icon={<PiTagLight />} title={t('Specializations')} className="mt-5">
            <div className="flex flex-wrap gap-2">
              {specializations.map((spec, index) => (
                <span key={index} className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 bg-surface-sunken/70 border border-hairline-soft rounded">
                  {spec}
                </span>
              ))}
            </div>
          </GlassCard>
        )}
        {languagesSpoken.length > 0 && (
          <GlassCard icon={<PiTranslateLight />} title={t('Languages Spoken')} className="mt-5">
            <div className="flex flex-wrap gap-2">
              {languagesSpoken.map((lang, index) => (
                <span key={index} className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 bg-surface-sunken/70 border border-hairline-soft rounded">
                  {lang}
                </span>
              ))}
            </div>
          </GlassCard>
        )}
        {lawyerInfo.Education && (
          <GlassCard icon={<PiBookOpenLight />} title={t('Education')} className="mt-5">
            <p className="text-sm text-gray-700 dark:text-gray-300">{lawyerInfo.Education}</p>
          </GlassCard>
        )}
        {(certifications.length > 0 || awards.length > 0 || publications.length > 0) && (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            {certifications.length > 0 && (
              <GlassCard icon={<PiCertificateLight />} title={t('Certifications')}>
                <ul className="space-y-1">
                  {certifications.map((cert, index) => (
                    <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                      {cert.fileUrl ? (
                        <a href={cert.fileUrl} target="_blank" rel="noopener noreferrer" className="text-info-600 hover:underline">{cert.title}</a>
                      ) : (
                        cert.title
                      )}
                    </li>
                  ))}
                </ul>
              </GlassCard>
            )}
            {awards.length > 0 && (
              <GlassCard icon={<PiTrophyLight />} title={t('Awards')}>
                <ul className="space-y-1">
                  {awards.map((award, index) => (
                    <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                      {award.fileUrl ? (
                        <a href={award.fileUrl} target="_blank" rel="noopener noreferrer" className="text-info-600 hover:underline">{award.title}</a>
                      ) : (
                        award.title
                      )}
                    </li>
                  ))}
                </ul>
              </GlassCard>
            )}
            {publications.length > 0 && (
              <GlassCard icon={<PiBookOpenLight />} title={t('Publications')}>
                <ul className="space-y-1">
                  {publications.map((pub, index) => (
                    <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                      {pub.fileUrl ? (
                        <a href={pub.fileUrl} target="_blank" rel="noopener noreferrer" className="text-info-600 hover:underline">{pub.title}</a>
                      ) : (
                        pub.title
                      )}
                    </li>
                  ))}
                </ul>
              </GlassCard>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderEngagementTermsTab = () => {
    return <ClientEngagementTerms lawyerId={lawyerId} />;
  };

  const renderCasesTab = () => {
    if (isLoadingCases) return renderSectionSkeleton();
    if (cases.length === 0) return <p className="text-gray-500">{t('No shared cases available')}</p>;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cases.map((caseDoc) => (
          <ListItem
            key={caseDoc.CaseID}
            title={caseDoc.CaseName || t('Case')}
            subtitle={`${t('Status')}: ${t(formatCamelCase(caseDoc.case_status)) || t('Unknown')}`}
            details={[
              { label: t('Created At'), value: convertLongDateWithTranslation(new Date(caseDoc.created_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: '2-digit' })) },
            ]}
            onClick={() => handleCaseClick(caseDoc)}
            link=""
          />
        ))}
      </div>
    );
  };

  const renderDocumentsTab = () => {
    if (isLoadingDocuments) return renderSectionSkeleton();
    return <Badge color="gray">{t('Coming soon')}</Badge>;
  };

  const renderBillingTab = () => {
    if (isLoadingBilling) return renderSectionSkeleton();
    return <Badge color="gray">{t('Coming soon')}</Badge>;
  };

  return (
    <div className="mx-4 pt-6 transition-all duration-300">
      <div className="flex items-start mb-4">
        <button
          onClick={() => history.push('/lawyers')}
          className="text-secondary-900 hover:text-secondary-600 transition-colors me-4"
        >
          <PiCaretLeftLight className="h-6 w-6 rtl:-scale-x-100" />
        </button>
        {renderProfile()}
      </div>
      <div className="mb-4">
        <TabStatusNav tabs={tabs} activeTab={activeTab} onTabClick={handleTabChange} />
      </div>
      <div className="h-[calc(100vh-260px)] overflow-y-auto">
        {activeTab === 'information' && renderInformationTab()}
        {activeTab === 'engagement' && renderEngagementTermsTab()}
        {activeTab === 'cases' && renderCasesTab()}
        {activeTab === 'documents' && renderDocumentsTab()}
        {activeTab === 'billing' && renderBillingTab()}
      </div>
    </div>
  );
};

export default LawyerDetails;